import {
    BankAccountRequest,
    CreateDocumentUploadUrlRequest,
    CreateDocumentUploadUrlResponse,
    DematAccountRequest,
    GetDematApplicationStatusResponse,
    InitiateDematApplicationResponse,
    KillSwitchStatusResponse,
    SetKillSwitchRequest,
    SetPrimaryBankAccountRequest,
    TradingAccount,
    UpsertNomineesRequest
} from '../../proto/generated/trading-bridge'
import api from './axiosClient'
import { providerCapabilities } from '../../util/backendProvider'
import {
    NomineeChangeRequest
} from '../../pages/settings/tradingAccountProfile/nomineeChangeRequest.types'
import {
    normalizeNomineeChangeRequest
} from '../../pages/settings/tradingAccountProfile/nomineeChangeRequest.utils'

const protobufHeaders = {
    Accept: 'application/x-protobuf'
}

const decodeTradingAccount = (data: ArrayBuffer): TradingAccount =>
    TradingAccount.decode(new Uint8Array(data))

let accountProfileRequest: Promise<TradingAccount> | null = null

const emptyTradingAccount = (): TradingAccount => TradingAccount.create({})

export const getAccountProfile = (): Promise<TradingAccount> => {
    if (!providerCapabilities.accountProfile) {
        return Promise.resolve(emptyTradingAccount())
    }
    if (!accountProfileRequest) {
        accountProfileRequest = api.get('/trading-accounts/profile', {
            headers: {
                ...protobufHeaders,
                'X-Suppress-Error': 'true'
            },
            responseType: 'arraybuffer'
        }).then((response) => decodeTradingAccount(response.data)).finally(() => {
            accountProfileRequest = null
        })
    }
    return accountProfileRequest
}

export const addBankAccount = async (request: BankAccountRequest): Promise<TradingAccount> => {
    if (!providerCapabilities.accountProfile) return emptyTradingAccount()
    const response = await api.post('/trading-accounts/bank-accounts', JSON.stringify(BankAccountRequest.toJSON(request)), {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return decodeTradingAccount(response.data)
}

export const updateBankAccount = async (request: BankAccountRequest): Promise<TradingAccount> => {
    if (!providerCapabilities.accountProfile) return emptyTradingAccount()
    const response = await api.put('/trading-accounts/bank-accounts', JSON.stringify(BankAccountRequest.toJSON(request)), {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return decodeTradingAccount(response.data)
}

export const removeBankAccount = async (id: number): Promise<TradingAccount> => {
    if (!providerCapabilities.accountProfile) return emptyTradingAccount()
    const response = await api.delete(`/trading-accounts/bank-accounts/${id}`, {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return decodeTradingAccount(response.data)
}

export const setPrimaryBankAccount = async (id: number): Promise<TradingAccount> => {
    if (!providerCapabilities.accountProfile) return emptyTradingAccount()
    const body = SetPrimaryBankAccountRequest.create({ id })
    const response = await api.post(`/trading-accounts/bank-accounts/${id}/make-primary`, JSON.stringify(SetPrimaryBankAccountRequest.toJSON(body)), {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return decodeTradingAccount(response.data)
}

export const upsertNominees = async (request: UpsertNomineesRequest): Promise<TradingAccount> => {
    if (!providerCapabilities.accountProfile) return emptyTradingAccount()
    const response = await api.put('/trading-accounts/nominees', JSON.stringify(UpsertNomineesRequest.toJSON(request)), {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return decodeTradingAccount(response.data)
}

export const getNomineeChangeRequest = async (): Promise<NomineeChangeRequest | null> => {
    if (!providerCapabilities.accountProfile) return null
    try {
        const response = await api.get('/trading-accounts/nominees/change-request', {
            headers: {
                Accept: 'application/json',
                'X-Suppress-Error': 'true'
            },
            responseType: 'json'
        })
        return normalizeNomineeChangeRequest(response.data)
    } catch {
        return null
    }
}

export const submitNomineeChangeRequest = async (request: UpsertNomineesRequest): Promise<NomineeChangeRequest> => {
    if (!providerCapabilities.accountProfile) {
        return { id: 0, status: 0, nominees: [], requestedAt: 0 }
    }
    const response = await api.put(
        '/trading-accounts/nominees/change-request',
        UpsertNomineesRequest.toJSON(request),
        {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            responseType: 'json'
        }
    )
    const normalized = normalizeNomineeChangeRequest(response.data)
    if (!normalized) {
        throw new Error('Invalid nominee change request response')
    }
    return normalized
}

export const addDematAccount = async (request: DematAccountRequest): Promise<TradingAccount> => {
    if (!providerCapabilities.accountProfile) return emptyTradingAccount()
    const response = await api.post('/trading-accounts/demat-accounts', JSON.stringify(DematAccountRequest.toJSON(request)), {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return decodeTradingAccount(response.data)
}

export const updateDematAccount = async (request: DematAccountRequest): Promise<TradingAccount> => {
    if (!providerCapabilities.accountProfile) return emptyTradingAccount()
    const response = await api.put('/trading-accounts/demat-accounts', JSON.stringify(DematAccountRequest.toJSON(request)), {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return decodeTradingAccount(response.data)
}

export const removeDematAccount = async (id: number): Promise<TradingAccount> => {
    if (!providerCapabilities.accountProfile) return emptyTradingAccount()
    const response = await api.delete(`/trading-accounts/demat-accounts/${id}`, {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return decodeTradingAccount(response.data)
}

export const createDocumentUploadUrl = async (request: CreateDocumentUploadUrlRequest): Promise<CreateDocumentUploadUrlResponse> => {
    if (!providerCapabilities.accountProfile) return CreateDocumentUploadUrlResponse.create({})
    const response = await api.post('/trading-accounts/documents/upload-url', JSON.stringify(CreateDocumentUploadUrlRequest.toJSON(request)), {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return CreateDocumentUploadUrlResponse.decode(new Uint8Array(response.data))
}

export const setKillSwitch = async (request: SetKillSwitchRequest): Promise<TradingAccount> => {
    if (!providerCapabilities.accountProfile) return emptyTradingAccount()
    const response = await api.post('/trading-accounts/kill-switch', JSON.stringify(SetKillSwitchRequest.toJSON(request)), {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return decodeTradingAccount(response.data)
}

export const getKillSwitchStatus = async (): Promise<KillSwitchStatusResponse> => {
    if (!providerCapabilities.accountProfile) return KillSwitchStatusResponse.create({})
    const response = await api.get('/trading-accounts/kill-switch', {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return KillSwitchStatusResponse.decode(new Uint8Array(response.data))
}

export const initiateDematApplication = async (): Promise<InitiateDematApplicationResponse> => {
    if (!providerCapabilities.accountProfile) return InitiateDematApplicationResponse.create({})
    const response = await api.post('/trading-accounts/demat/apply', null, {
        headers: {
            ...protobufHeaders,
            'X-Suppress-Error': 'true'
        },
        responseType: 'arraybuffer'
    })
    return InitiateDematApplicationResponse.decode(new Uint8Array(response.data))
}

export const getDematApplicationStatus = async (): Promise<GetDematApplicationStatusResponse> => {
    if (!providerCapabilities.accountProfile) return GetDematApplicationStatusResponse.create({})
    const response = await api.get('/trading-accounts/demat/status', {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return GetDematApplicationStatusResponse.decode(new Uint8Array(response.data))
}
